import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../lib/apiClient';
import type { User, DatacenterRecord, DatacenterUserRecord, BroadcastUsersResponse } from '../types';

interface UserDetailsResponse {
  user: User;
  datacenterRecords: DatacenterRecord[];
}

/** Broadcast API returns either wrapped response or array directly */
type BroadcastUserResponse = BroadcastUsersResponse | DatacenterUserRecord[];

const fetchUserDetails = async (uuid: string): Promise<UserDetailsResponse> => {
  // Fetch user data from all datacenters
  const response = await apiClient().get(`broadcast/api/users/${uuid}`).json<BroadcastUserResponse>();

  // Handle broadcast API response structure (wrapper with .data or raw array)
  const datacenterUsers: DatacenterUserRecord[] = Array.isArray(response) ? response : response.data || [];

  if (datacenterUsers.length === 0) {
    throw new Error('No user data found in response');
  }

  // Use the first record for primary user details (typically the source datacenter)
  const primaryRecord = datacenterUsers[0];

  // Build the user object from the primary record
  const userData: User = {
    id: primaryRecord.id,
    authServiceUuid: primaryRecord.authServiceUuid,
    emailAddress: primaryRecord.emailAddress,
    accountId: null, // Not in broadcast response
    divisionIds: [], // Not in broadcast response
    firstName: primaryRecord.firstName,
    lastName: primaryRecord.lastName,
    status: typeof primaryRecord.status === 'string' ? parseInt(primaryRecord.status) : primaryRecord.status,
    title: primaryRecord.title,
    primaryPhoneNumber: primaryRecord.primaryPhoneNumber,
    secondaryPhoneNumber: primaryRecord.secondaryPhoneNumber,
    fiscalYearEnd: primaryRecord.fiscalYearEnd,
    arDepartmentEmailAddress: primaryRecord.arDepartmentEmailAddress,
    country: primaryRecord.country,
    postalCode: primaryRecord.postalCode,
    role: primaryRecord.role,
    language: primaryRecord.locale,
    industry: primaryRecord.industry,
    supplierReadOnly: primaryRecord.supplierReadOnly,
    created: primaryRecord.created,
    updated: primaryRecord.updated,
    termsCondsFlag: primaryRecord.termsCondsFlag,
    emailVerified: primaryRecord.emailVerified,
    registrationMethod: primaryRecord.registrationMethod,
    createdSource: primaryRecord.createdSource,
    createdOn: null, // Not in broadcast response
    updatedOn: null, // Not in broadcast response
    deletedOn: null, // Not in broadcast response
    forgottenOn: primaryRecord.forgottenOn,
    leadSource: primaryRecord.leadSource,
    source: null, // Will be set below
    datacenters: [],
    c2foDbId: null, // Not in broadcast response
    contactPreference: primaryRecord.contactPreference,
    fundingPartnerTermsCount: null, // Not in broadcast response
    fundingPartnerCount: null, // Not in broadcast response
  };

  // Just use the datacenter records directly
  const userDatacenters: User['datacenters'] = datacenterUsers;

  // Get the source datacenter name
  const sourceValue = datacenterUsers[0]?.source || null;

  // Create datacenter records for the DatacentersTab component
  const datacenterRecords: DatacenterRecord[] = datacenterUsers.map((dc, index) => ({
    datacenter: dc.source,
    authServiceUuid: dc.authServiceUuid,
    status: typeof dc.status === 'string' ? parseInt(dc.status) : dc.status,
    termsCondsFlag: dc.termsCondsFlag,
    emailVerified: dc.emailVerified,
    registrationDate: dc.registrationDate,
    forgottenOn: dc.forgottenOn,
    supplierReadOnly: dc.supplierReadOnly,
    authUser: index === 0 ? { isSource: true } : undefined,
  }));

  const user = {
    ...userData,
    source: sourceValue,
    datacenters: userDatacenters,
  };

  return { user, datacenterRecords };
};

export const useUserDetailsQuery = (uuid: string | undefined) => {
  return useQuery({
    queryKey: ['userDetails', uuid],
    queryFn: () => fetchUserDetails(uuid!),
    enabled: !!uuid,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};
